<div class="checkin-container">
  <div class="checkin-topbar">
    <div class="topbar-left">
      <h1>
        Equipment Check-In
        <small>Returns and overdue follow-up for equipment currently out on rental</small>
      </h1>
      <div class="breadcrumb">
        <i class="bi bi-house-door"></i> IMS / <span>Equipment Rental</span> / <span class="current">Check-In /
          Returns</span>
        <span class="separator">|</span>
        <span class="time"><i class="bi bi-clock"></i> {{ timestamp() }}</span>
      </div>
    </div>
    <div class="topbar-right">
      <div class="search-box">
        <i class="bi bi-search"></i>
        <input type="text" placeholder="Search checked-out assets..." [ngModel]="searchQuery()" (ngModelChange)="searchQuery.set($event)" />
      </div>
      <button class="btn btn-outline btn-sm" (click)="exportData()">
        <i class="bi bi-download"></i> Export
      </button>
      @if (overdueStats().total > 0) {
        <button class="btn btn-danger btn-sm" (click)="sendAllNotifications()">
          <i class="bi bi-bell"></i> Notify All
        </button>
      }
      <button class="btn btn-primary" (click)="openReturnModal()">
        <i class="bi bi-arrow-return-left"></i> Process Return
      </button>
    </div>
  </div>

  <div class="stats-row">
    <div class="stat-card">
      <div class="stat-icon blue">
        <i class="bi bi-box-seam"></i>
      </div>
      <div class="stat-info">
        <div class="stat-label">Checked Out</div>
        <div class="stat-value">{{ stats().checkedOut }}</div>
      </div>
    </div>
    <div class="stat-card">
      <div class="stat-icon green">
        <i class="bi bi-calendar-check"></i>
      </div>
      <div class="stat-info">
        <div class="stat-label">Due Today</div>
        <div class="stat-value">{{ stats().dueToday }}</div>
      </div>
    </div>
    <div class="stat-card">
      <div class="stat-icon amber">
        <i class="bi bi-clock"></i>
      </div>
      <div class="stat-info">
        <div class="stat-label">1–3 Days Overdue</div>
        <div class="stat-value">{{ overdueStats().short }}</div>
      </div>
    </div>
    <div class="stat-card">
      <div class="stat-icon orange">
        <i class="bi bi-clock"></i>
      </div>
      <div class="stat-info">
        <div class="stat-label">4–7 Days Overdue</div>
        <div class="stat-value">{{ overdueStats().medium }}</div>
      </div>
    </div>
    <div class="stat-card">
      <div class="stat-icon dark-red">
        <i class="bi bi-clock"></i>
      </div>
      <div class="stat-info">
        <div class="stat-label">7+ Days Overdue</div>
        <div class="stat-value">{{ overdueStats().long }}</div>
      </div>
    </div>
  </div>

  @if (overdueStats().total > 0) {
    <div class="alert-banner danger">
      <i class="bi bi-exclamation-triangle-fill"></i>
      <div>
        <strong>{{ overdueStats().total }} items overdue</strong>
        <span>| Process returns here or notify customers to avoid penalties</span>
      </div>
      <button type="button" class="btn btn-danger btn-sm" (click)="viewAllOverdue()">View All Overdue</button>
    </div>
  }

  <div class="filter-bar">
    <div class="filter-left">
      <div class="filter-group">
        <label><i class="bi bi-tag"></i> Status</label>
        <select [ngModel]="statusFilter()" (ngModelChange)="statusFilter.set($event)">
          <option value="">All Status</option>
          <option value="active">Checked Out</option>
          <option value="overdue">Overdue</option>
        </select>
      </div>
      <div class="filter-group">
        <label><i class="bi bi-clock-history"></i> Overdue Period</label>
        <select [ngModel]="periodFilter()" (ngModelChange)="periodFilter.set($event)">
          <option value="all">All Periods</option>
          <option value="short">1–3 Days</option>
          <option value="medium">4–7 Days</option>
          <option value="long">7+ Days</option>
        </select>
      </div>
      <div class="filter-group">
        <label><i class="bi bi-building"></i> Branch</label>
        <select [ngModel]="branchFilter()" (ngModelChange)="branchFilter.set($event)">
          @if (!branchFilterLocked()) {
            <option value="all">All Branches</option>
          }
          @for (branch of branches(); track branch.id) {
            <option [value]="branch.name">{{ branch.name }}</option>
          }
        </select>
      </div>
      <div class="filter-group">
        <label><i class="bi bi-geo-alt"></i> Location</label>
        <select [ngModel]="locationFilter()" (ngModelChange)="locationFilter.set($event)">
          <option value="all">All Locations</option>
          @for (location of locations(); track location.id) {
            <option [value]="location.name">{{ location.name }}</option>
          }
        </select>
      </div>
    </div>
    <div class="filter-right">
      <span class="filter-count">Showing {{ activeCheckouts().length }} assets</span>
    </div>
  </div>

  <div class="table-wrap" #checkinTable>
    <div class="table-scroll">
      <table>
        <thead>
          <tr>
            <th>Asset</th>
            <th>Customer</th>
            <th>Destination</th>
            <th>Qty out</th>
            <th>Returned</th>
            <th>Outstanding</th>
            <th>Contact</th>
            <th>Receiver</th>
            <th>Branch</th>
            <th>Store</th>
            <th>Checked Out</th>
            <th>Due Date</th>
            <th>Days Left</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (checkout of activeCheckouts(); track checkout.id) {
            <tr [class]="isCheckoutOverdue(checkout) ? overdueSeverity(daysOverdue(checkout)).rowClass : ''">
              <td>
                <div class="item-info">
                  <span class="item-icon"><i class="bi" [class]="findAssetIcon(checkout.equipment)"></i></span>
                  <div>
                    <div class="item-name">{{ checkout.equipment }}</div>
                    <div class="item-id">{{ findAssetId(checkout.equipment) }}</div>
                  </div>
                </div>
              </td>
              <td>{{ checkout.customer }}</td>
              <td>{{ checkout.destination || '—' }}</td>
              <td>{{ checkout.quantity ?? 1 }}</td>
              <td>{{ checkout.quantityReturned ?? 0 }}</td>
              <td>{{ checkout.quantityOutstanding ?? checkout.quantity ?? 1 }}</td>
              <td>{{ checkout.receiverPhone || '—' }}</td>
              <td>{{ checkout.receiver || '—' }}</td>
              <td><span class="branch-tag">{{ checkout.branch || '—' }}</span></td>
              <td><span class="location-tag">{{ checkout.location || '—' }}</span></td>
              <td>{{ formatDate(checkout.checkoutDate) }}</td>
              <td>{{ formatDate(checkout.dueDate) }}</td>
              <td><span class="days-left" [class]="daysLeft(checkout).className">{{ daysLeft(checkout).label }}</span></td>
              <td>
                @if (isCheckoutOverdue(checkout)) {
                  <span class="status-badge" [class]="overdueSeverity(daysOverdue(checkout)).badgeClass">{{ checkoutStatusLabel(checkout) }}</span>
                } @else {
                  <span class="status-badge checked-out">{{ checkoutStatusLabel(checkout) }}</span>
                }
              </td>
              <td class="actions-cell">
                <div class="row-actions">
                  @if (checkout.inventoryItemID) {
                    <a
                      class="btn-action track"
                      title="Track this item"
                      [routerLink]="['/item-history', checkout.inventoryItemID]"
                    >
                      <i class="bi bi-geo-alt"></i>
                    </a>
                  }
                  <button type="button" class="btn-action edit" title="Edit checkout" (click)="openEditModal(checkout)">
                    <i class="bi bi-pencil"></i>
                  </button>

                </div>
                <button class="btn btn-success btn-sm" (click)="openReturnModal(checkout)">
                  <i class="bi bi-arrow-return-left"></i> Return
                </button>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </div>

  <div class="pagination-bar">
    <div class="pagination-info">
      Showing <span>1</span> to <span>{{ activeCheckouts().length }}</span> of <span>{{ activeCheckouts().length }}</span> assets
    </div>
  </div>
</div>

<!-- Return Modal -->
<div class="modal-overlay" [class.open]="showReturnModal()" (click)="closeModalOnBackdrop($event)">
  <div class="modal modal-large">
    <div class="modal-header">
      <h2><i class="bi bi-arrow-return-left"></i> Process Return</h2>
      <button class="close-btn" (click)="closeReturnModal()">
        <i class="bi bi-x-lg"></i>
      </button>
    </div>

    <div class="modal-body">
      @if (selectedCheckout(); as checkout) {
        @if (isCheckoutOverdue(checkout)) {
          <div class="alert-banner warning">
            <i class="bi bi-exclamation-triangle-fill"></i>
            <span>This item is <strong>{{ daysOverdue(checkout) }} days overdue</strong>. Late fees may apply.</span>
          </div>
        }

        <div class="checkout-summary">
          <div class="form-row">
            <div class="form-group">
              <label>Checkout No.</label>
              <input type="text" [value]="checkout.checkoutNumber" disabled />
            </div>
            <div class="form-group">
              <label>Equipment</label>
              <input type="text" [value]="checkout.equipment" disabled />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Customer</label>
              <input type="text" [value]="checkout.customer" disabled />
            </div>
            <div class="form-group">
              <label>Contact Phone</label>
              <input type="text" [value]="checkout.receiverPhone || '—'" disabled />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Destination</label>
              <input type="text" [value]="checkout.destination || '—'" disabled />
            </div>
            <div class="form-group">
              <label>Quantity tracking</label>
              <input
                type="text"
                [value]="
                  'Out ' +
                  (checkout.quantity ?? 1) +
                  ' · Returned ' +
                  (checkout.quantityReturned ?? 0) +
                  ' · Outstanding ' +
                  (checkout.quantityOutstanding ?? checkout.quantity ?? 1)
                "
                disabled
              />
            </div>
          </div>
        </div>
      }

      <div class="form-row">
        <div class="form-group">
          <label>Asset <span class="required">*</span></label>
          <select [ngModel]="selectedCheckoutId()" (ngModelChange)="onReturnCheckoutSelect($event)">
            <option value="">Select Asset</option>
            @for (checkout of activeCheckouts(); track checkout.id) {
              <option [value]="checkout.id">{{ findAssetId(checkout.equipment) }} - {{ checkout.equipment }}</option>
            }
          </select>
        </div>
        <div class="form-group">
          <label>Return Date <span class="required">*</span></label>
          <input type="date" [ngModel]="returnDate()" (ngModelChange)="returnDate.set($event)" />
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label>Qty returning <span class="required">*</span></label>
          <input
            type="number"
            min="0"
            step="1"
            appDigitsOnly
            [max]="returnQuantityMax()"
            [ngModel]="returnQuantity()"
            (ngModelChange)="onReturnQuantityChange($event)"
          />
          <small class="field-hint">
            Outstanding: {{ returnQuantityMax() }}. Enter how many are physically coming back
            (0 if none — then mark the rest lost or damaged below).
          </small>
        </div>
        <div class="form-group">
          <label>Brought back by <span class="required">*</span></label>
          <input type="text" placeholder="Person returning the equipment..." [ngModel]="returnReceiver()" (ngModelChange)="returnReceiver.set($event)" />
        </div>
      </div>

      @if (showUnreturnedFate()) {
        <div class="form-row">
          <div class="form-group full unreturned-fate">
            <label>
              What about the remaining {{ unreturnedQuantity() }}?
              <span class="required">*</span>
            </label>
            <div class="status-options unreturned-options">
              <label class="status-option">
                <input
                  type="radio"
                  name="unreturnedFate"
                  value="still-out"
                  [checked]="unreturnedFate() === 'still-out'"
                  (change)="unreturnedFate.set('still-out')"
                />
                <span>Still out — return later</span>
              </label>
              <label class="status-option">
                <input
                  type="radio"
                  name="unreturnedFate"
                  value="lost"
                  [checked]="unreturnedFate() === 'lost'"
                  (change)="unreturnedFate.set('lost')"
                />
                <span>Lost / missing</span>
              </label>
              <label class="status-option">
                <input
                  type="radio"
                  name="unreturnedFate"
                  value="damaged"
                  [checked]="unreturnedFate() === 'damaged'"
                  (change)="unreturnedFate.set('damaged')"
                />
                <span>Damaged — write off (Damaged / Lost)</span>
              </label>
            </div>
            <small class="field-hint">{{ unreturnedFateHint() }}</small>
          </div>
        </div>
      }

      <div class="form-row">
        <div class="form-group">
          <label>Received by <span class="required">*</span></label>
          <input type="text" placeholder="Staff who received it..." [ngModel]="returnReceivedBy()" (ngModelChange)="returnReceivedBy.set($event)" />
        </div>
        <div class="form-group">
          <label>Branch <span class="required">*</span></label>
          <select [ngModel]="returnBranch()" (ngModelChange)="returnBranch.set($event)">
            <option value="">Select Branch</option>
            @for (branch of branches(); track branch.id) {
              <option [value]="branch.name">{{ branch.name }}</option>
            }
          </select>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label>Store Location <span class="required">*</span></label>
          <select [ngModel]="returnLocation()" (ngModelChange)="returnLocation.set($event)">
            <option value="">Select Location</option>
            @for (location of locations(); track location.id) {
              <option [value]="location.name">{{ location.name }}</option>
            }
          </select>
          <small class="field-hint">Returned stock is added to this location on Current Stock / Item Master.</small>
        </div>
        <div class="form-group">
          <label>
            Condition of returned items
            @if (returnQuantity() > 0) {
              <span class="required">*</span>
            }
          </label>
          <select [ngModel]="returnCondition()" (ngModelChange)="returnCondition.set($event)">
            <option value="">Select Condition</option>
            @for (condition of conditionOptions(); track condition) {
              <option [value]="condition">{{ condition }}</option>
            }
          </select>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group full">
          <label>Return Notes</label>
          <textarea rows="2" placeholder="Add any notes about the return..." [ngModel]="returnNotes()" (ngModelChange)="returnNotes.set($event)"></textarea>
        </div>
      </div>
    </div>

    <div class="modal-footer">
      <button class="btn btn-outline" (click)="closeReturnModal()">Cancel</button>
      <button class="btn btn-success" (click)="confirmReturn()">
        <i class="bi bi-check-lg"></i> Complete Return
      </button>
      <button class="btn btn-danger" (click)="reportDamaged()">
        <i class="bi bi-exclamation-triangle"></i> Report Damaged
      </button>
    </div>
  </div>
</div>

<!-- Edit Modal -->
<div class="modal-overlay" [class.open]="showEditModal()" (click)="closeModalOnBackdrop($event)">
  <div class="modal">
    <div class="modal-header">
      <h2><i class="bi bi-pencil"></i> Edit Checkout</h2>
      <button class="close-btn" (click)="closeEditModal()">
        <i class="bi bi-x-lg"></i>
      </button>
    </div>

    <div class="modal-body">
      <div class="form-row">
        <div class="form-group">
          <label>Customer / Case <span class="required">*</span></label>
          <input type="text" [ngModel]="editCustomer()" (ngModelChange)="editCustomer.set($event)" />
        </div>
        <div class="form-group">
          <label>Due Date <span class="required">*</span></label>
          <input type="date" [ngModel]="editDueDate()" (ngModelChange)="editDueDate.set($event)" />
        </div>
      </div>
    </div>

    <div class="modal-footer">
      <button class="btn btn-outline" (click)="closeEditModal()">Cancel</button>
      <button class="btn btn-primary" (click)="saveCheckout()">
        <i class="bi bi-check-lg"></i> Save Changes
      </button>
    </div>
  </div>
</div>

<!-- Toast -->
<div class="toast-container" [class.show]="showToast()">
  <div
    class="toast"
    [class.success]="toastType() === 'success'"
    [class.error]="toastType() === 'error'"
    [class.info]="toastType() === 'info'"
  >
    <div class="toast-icon">
      @if (toastType() === 'error') {
        <i class="bi bi-x-circle-fill"></i>
      } @else if (toastType() === 'info') {
        <i class="bi bi-info-circle-fill"></i>
      } @else {
        <i class="bi bi-check-circle-fill"></i>
      }
    </div>
    <div class="toast-content">
      <div class="toast-title">
        {{ toastType() === 'error' ? 'Error' : toastType() === 'info' ? 'Notice' : 'Success!' }}
      </div>
      <div class="toast-message">{{ toastMessage() }}</div>
    </div>
    <button class="toast-close" (click)="closeToast()">
      <i class="bi bi-x-lg"></i>
    </button>
  </div>
</div>
